import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Paper, Chip, CircularProgress, Alert, Button } from '@mui/material';
import { taskService } from '../../../../services/taskService';
import { Task } from '../../../../types/task';
import { TaskStatus } from '../../../../types/task-status'; // Изменено с '../../../../types/task'
import { TaskUserDTO, TaskUserStatus } from '../../../../types/task-user'; // Импортируем TaskUserDTO и TaskUserStatus
import { ChatScope, ChatType, ChatMessageType } from '../../../../types/chat-enums';
import { useAuth } from '../../../../contexts/AuthContext';
import { UserType } from '../../../../types/auth';
import { Media } from '../../../../types/media';
import { TaskSkillDTO } from '../../../../types/task-skill';
import { format } from 'date-fns';
import  ru  from 'date-fns/locale/ru';
import { AssignmentInd, Chat, Description, InsertPhoto, Link } from '@mui/icons-material';
import { blue, grey } from '@mui/material/colors';
import { Container } from '@mui/material';
import styles from './ExecutorTaskDetailsPage.module.css';
// import TaskChat from '../../../../components/chat/TaskChat'; // Импортируем компонент чата
import TaskChatModal from '../../../../components/chat/TaskChatModal';

const ExecutorTaskDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const userId = authenticatedUser?.type === UserType.USER ? authenticatedUser.user?.id : undefined;

    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentUserTaskUser, setCurrentUserTaskUser] = useState<TaskUserDTO | null>(null); // Добавляем состояние для TaskUser текущего пользователя
    const [isChatModalOpen, setIsChatModalOpen] = useState<boolean>(false);

    useEffect(() => {
        console.log('ExecutorTaskDetailsPage: useEffect triggered');
        console.log('ExecutorTaskDetailsPage: taskId from useParams', id);
        console.log('ExecutorTaskDetailsPage: userId from auth context', userId);
        const fetchTask = async () => {
            if (!id || !userId) {
                setError('Не удалось загрузить детали задачи: ID задачи или пользователя отсутствует.');
                setLoading(false);
                console.log('ExecutorTaskDetailsPage: Missing ID or userId, not fetching task');
                return;
            }
            try {
                setLoading(true);
                console.log('ExecutorTaskDetailsPage: Fetching task with ID', id);
                const data = await taskService.getTaskById(id, userId);
                setTask(data);
                setCurrentUserTaskUser(data.taskUsers?.find(tu => tu.userId === userId) || null); // Устанавливаем TaskUser текущего пользователя
                console.log('ExecutorTaskDetailsPage: Task fetched successfully', data);
            } catch (err) {
                setError('Не удалось загрузить детали задачи.');
                console.error('ExecutorTaskDetailsPage: Error fetching task', err);
            } finally {
                setLoading(false);
                console.log('ExecutorTaskDetailsPage: Task fetch finished');
            }
        };

        fetchTask();
    }, [id, userId, authenticatedUser]); // Добавляем authenticatedUser в зависимости

    const handleAcceptTask = async () => {
        if (!currentUserTaskUser?.id) return;
        try {
            await taskService.acceptTask(currentUserTaskUser.id);
            // Обновляем локальное состояние задачи
            setTask((prevTask: Task | null) => {
                if (!prevTask) return null;
                const updatedTaskUsers = prevTask.taskUsers?.map((tu: TaskUserDTO) => 
                    tu.id === currentUserTaskUser.id ? { ...tu, status: TaskUserStatus.Working } : tu
                );
                return { ...prevTask, taskUsers: updatedTaskUsers, status: TaskStatus.InProgress }; // Обновляем статус задачи и TaskUser
            });
            setCurrentUserTaskUser((prev: TaskUserDTO | null) => prev ? { ...prev, status: TaskUserStatus.Working } : null);
        } catch (error) {
            console.error("Ошибка при принятии задачи:", error);
            setError("Не удалось принять задачу.");
        }
    };

    const handleRejectTask = async () => {
        if (!currentUserTaskUser?.id) return;
        try {
            await taskService.rejectTask(currentUserTaskUser.id);
            // Обновляем локальное состояние задачи
            setTask((prevTask: Task | null) => {
                if (!prevTask) return null;
                const updatedTaskUsers = prevTask.taskUsers?.map((tu: TaskUserDTO) => 
                    tu.id === currentUserTaskUser.id ? { ...tu, status: TaskUserStatus.Rejected } : tu
                );
                return { ...prevTask, taskUsers: updatedTaskUsers }; // Обновляем только TaskUser, статус задачи не меняется
            });
            setCurrentUserTaskUser((prev: TaskUserDTO | null) => prev ? { ...prev, status: TaskUserStatus.Rejected } : null);
        } catch (error) {
            console.error("Ошибка при отклонении задачи:", error);
            setError("Не удалось отклонить задачу.");
        }
    };

    const getStatusChipColor = (status: TaskStatus) => {
        switch (status) {
            case TaskStatus.Paid:
            case TaskStatus.Completed:
            case TaskStatus.Verified:
                return "success";
            case TaskStatus.Review:
                return "warning";
            case TaskStatus.Rejected:
            case TaskStatus.Cancelled:
                return "error";
            case TaskStatus.InProgress:
            case TaskStatus.Assigned:
            case TaskStatus.Selected:
            case TaskStatus.Confirmed:
                return "info";
            default:
                return "default";
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!task) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Задача не найдена.</Alert></Box>;
    }

    return (
        <Container maxWidth="lg" className={styles.pageContainer}>
            <Paper elevation={0} sx={{ p: 4, mb: 4 }}> {/* Elevation 0 to match screenshot's flat look */}
                <Box sx={{ p: 3 }}>
                    {/* Header Section: Date, Title, Price */}
                    <Box className={styles.headerContainer}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Дата создания: {task.createdAt ? format(new Date(task.createdAt), 'dd.MM.yyyy', { locale: ru }) : '-'}
                            </Typography>
                            <Typography variant="h4" className={styles.taskTitle}>
                                {task.title}
                            </Typography>
                        </Box>
                        <Button variant="contained" className={styles.priceButton}>
                            {task.priceInKopecks ? `${task.priceInKopecks / 100}₽` : '0₽'}
                        </Button>
                    </Box>
                    
                    {/* Task Details Section: Category, Status, Acceptance, Deadline, Customer */}
                    <Paper elevation={0} className={styles.infoCard}>
                        <Box className={styles.infoItem}>
                            <Typography variant="subtitle2" className={styles.infoLabel}>Категория</Typography>
                            <Chip label={task.category?.title || 'Не указана'} className={styles.categoryChip} size="small" />
                        </Box>
                        <Box className={styles.infoItem}>
                            <Typography variant="subtitle2" className={styles.infoLabel}>Статус</Typography>
                            <Chip label={task.status} className={styles.statusChip} color={getStatusChipColor(task.status)} size="small" />
                        </Box>
                        <Box className={styles.infoItem}>
                            <Typography variant="subtitle2" className={styles.infoLabel}>Принятие</Typography>
                            <Typography variant="body1" className={styles.infoValue}>
                                {task.assignedAt ? format(new Date(task.assignedAt), 'dd.MM.yyyy', { locale: ru }) : '—'}
                            </Typography>
                        </Box>
                        <Box className={styles.infoItem}>
                            <Typography variant="subtitle2" className={styles.infoLabel}>Дедлайн</Typography>
                            <Typography 
                                variant="body1" 
                                className={styles.infoValue}
                                sx={{ color: task.deadline && new Date(task.deadline) < new Date() ? 'red' : 'inherit' }}
                            >
                                {task.deadline ? format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru }) : '—'}
                            </Typography>
                        </Box>
                        <Box className={styles.infoItem}>
                            <Typography variant="subtitle2" className={styles.infoLabel}>Заказчик</Typography>
                            <Typography variant="body1" className={styles.infoValue}>{task.organization?.name || '—'}</Typography>
                        </Box>
                    </Paper>
                    
                    {/* Curator Block */}
                    <Box className={styles.curatorBlock}>
                        <AssignmentInd sx={{ fontSize: 60, color: grey[500] }} className={styles.curatorAvatar} /> {/* Универсальная иконка куратора */}
                        <Box className={styles.curatorInfo}>
                            <Typography variant="body2" className={styles.curatorLabel}>Куратор</Typography>
                            <Typography variant="h6" className={styles.curatorName}>
                                {task.manager?.fullName || 'Неизвестно'}
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<Chat />} className={styles.askQuestionButton} onClick={() => setIsChatModalOpen(true)}>
                            ЗАДАТЬ ВОПРОС ПО ЗАДАЧЕ
                        </Button>
                    </Box>
                    
                    {/* Description Section */}
                    <Typography variant="h5" className={styles.sectionTitle}>Описание задачи:</Typography>
                    <Typography variant="body1" className={styles.descriptionText}>
                        {task.description}
                    </Typography>
                    
                    {/* Skills Section */}
                    {task.taskSkills && task.taskSkills.length > 0 && (
                        <Box>
                            <Typography variant="h5" className={styles.sectionTitle}>Скилы для задачи:</Typography>
                            <Box className={styles.skillsContainer}>
                                {task.taskSkills.map((ts: TaskSkillDTO, index: number) => (
                                    <Chip key={index} label={ts.skill?.title || ''} className={styles.skillChip} />
                                ))}
                            </Box>
                        </Box>
                    )}
                    
                    {/* Files Section */}
                    {task.media && task.media.length > 0 && (
                        <Box className={styles.filesContainer}>
                            <Box className={styles.filesHeader}>
                                <Typography variant="h5" className={styles.sectionTitle}>Загруженные файлы:</Typography>
                                <Button className={styles.downloadAllButton}>Скачать все</Button>
                            </Box>
                            {task.media.map((mediaItem: Media) => (
                                <Paper key={mediaItem.id} elevation={0} className={styles.fileItem}>
                                    <Box className={styles.fileInfo}>
                                        {mediaItem.fileUrl?.includes('.pdf') || mediaItem.name.includes('.doc') || mediaItem.name.includes('.xls') ? <Description className={styles.fileIcon} /> : 
                                         mediaItem.fileUrl?.includes('.jpg') || mediaItem.name.includes('.png') || mediaItem.name.includes('.jpeg') ? <InsertPhoto className={styles.fileIcon} /> : 
                                         <Link className={styles.fileIcon} />}
                                        <Typography variant="body1" className={styles.fileName}>{mediaItem.name}</Typography>
                                    </Box>
                                    <Button size="small" variant="contained" href={mediaItem.fileUrl || '#'} target="_blank" rel="noopener noreferrer" className={styles.downloadButton}>
                                        Скачать
                                    </Button>
                                </Paper>
                            ))}
                        </Box>
                    )}
                    {/* Чат */}
                    {/* {id && <TaskChat taskId={id} />} */}
                </Box>
            </Paper>

            {/* Уведомляющая панель */}
            {currentUserTaskUser?.status === TaskUserStatus.Assigned && (
                <Paper elevation={3} sx={{ p: 2, mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: blue[50] }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Прочитал ТЗ полностью и готов принять задачу?</Typography>
                        <Typography variant="body2">
                            Появилась новая задача! Решай сам: хочешь взять её и попробовать свои силы прямо сейчас, или пропускаешь и ждёшь следующую возможность, которая может быть даже интереснее?
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="error" onClick={handleRejectTask}>Отклонить</Button>
                        <Button variant="contained" color="primary" onClick={handleAcceptTask}>Принять задачу</Button>
                    </Box>
                </Paper>
            )}

            {task && task.id && (
                <TaskChatModal
                    taskId={task.id}
                    open={isChatModalOpen}
                    onClose={() => setIsChatModalOpen(false)}
                    chatType={ChatType.TaskSupport}
                    chatScope={ChatScope.Task}
                    initiatorId={userId}
                />
            )}
        </Container>
    );
};

export default ExecutorTaskDetailsPage;
